import dayjs from 'dayjs';
import {
  LockerCard as LockerCardContainer,
  LockerCardHeader,
  LockerCardBody,
  LockerCardFooter,
  LockerNumber,
  MemberName,
  DateText,
  EmptyDateText,
  OverdueDateText,
  OverdueDaysText,
  StatusBadge,
  StatusText,
} from './LockerManagementStyled';

// Constants - Figma 디자인 기준
const STATUS_CONFIG = {
  inUse: {
    label: '사용중',
    bgColor: '#FFFFFF',
    border: '1px solid #4A6A94',
    textColor: '#262626',
    badgeBorder: '1px solid #4A6A94',
    badgeColor: '#4A6A94',
  },
  expired: {
    label: '라커만료',
    bgColor: '#EE735A',
    textColor: '#FFF',
    badgeBorder: '1px solid #FFF',
    badgeColor: '#fff',
  },
  expiringSoon: {
    label: '만료예정',
    bgColor: '#FFFFFF',
    border: '1px solid #EE735A',
    textColor: '#262626',
    badgeBorder: '1px solid #EE735A',
    badgeColor: '#EE735A',
  },
  unregistered: {
    label: '사용가능',
    bgColor: '#DCE3EC',
    border: 'none',
    textColor: '#262626',
    badgeBorder: '1px solid #262626',
    badgeColor: '#262626',
  },
  unavailable: {
    label: '사용불가',
    bgColor: '#d9d9d9',
    border: 'none',
    textColor: '#737373',
    badgeBorder: '1px solid #A6A6A6',
    badgeColor: '#A6A6A6',
  },
  // 아직 시작 전인 이용권만 있는 자리 — 회원상세 '사용예정'과 같은 초록 톤.
  upcoming: {
    label: '사용예정',
    bgColor: '#FFFFFF',
    border: '1px solid #4A6A94',
    textColor: '#262626',
    badgeBorder: '1px solid #4A6A94',
    badgeColor: '#4A6A94',
  },
  // 연결된 라커 이용권이 중지(STOPPED) 상태 — 회원상세 중지 카드와 톤을 맞춘 회색 계열.
  stopped: {
    label: '중지',
    bgColor: '#F0F0F0',
    border: '1px solid #A6A6A6',
    textColor: '#737373',
    badgeBorder: '1px solid #737373',
    badgeColor: '#737373',
  },
};

// Helper Functions
const getLockerStatus = locker => {
  // useYn이 'N'이면 사용불가 상태
  if (locker.useYn === 'N') {
    return 'unavailable';
  }
  // 연결된 라커 이용권이 중지 상태면 '중지' (만료/만료예정 판정보다 우선)
  if (locker.passStatus === 'STOPPED') {
    return 'stopped';
  }
  // 등록 회원 또는 미등록 회원 모두 없으면 '미등록' 상태
  if (!locker.memberName && !locker.unregisteredMemberName) {
    return 'unregistered';
  }

  const expireDate = locker.expireDate ? new Date(locker.expireDate) : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (!expireDate) {
    return 'inUse';
  }

  // 아직 시작 전인 이용권만 있는 자리는 '사용중'이 아니라 '사용예정'.
  // (issueDate 는 자리 캐시의 MIN(START_DATE) — 그게 미래면 지금 쓰는 이용권이 없다)
  // new Date('YYYY-MM-DD') 는 UTC 자정으로 파싱돼 KST 에선 하루 밀리므로 문자열끼리 비교한다.
  const todayText = dayjs().format('YYYY-MM-DD');
  const issueText = locker.issueDate ? String(locker.issueDate).slice(0, 10) : null;
  if (issueText && issueText > todayText) {
    return 'upcoming';
  }

  // 종료일도 문자열로 비교한다. new Date('YYYY-MM-DD') 는 UTC 자정이라 KST 에선 그날 09:00 이 되고,
  // 그 차이를 Math.ceil 로 올리면 만료 당일 하루가 -0.x → 0 이 되어 '만료임박' 으로 잡혔다.
  const expireText = String(locker.expireDate).slice(0, 10);
  if (expireText < todayText) {
    return 'expired';
  }
  const diffDays = dayjs(expireText).diff(dayjs(todayText), 'day');
  if (diffDays <= 10) {
    return 'expiringSoon';
  }
  return 'inUse';
};

// 이어지는(겹치거나 다음날 시작) 기간은 하나로 병합 — 실제 공백만 분리해서 보여준다
const mergeAdjacentPeriods = periods => {
  const sorted = [...periods].sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''));
  const merged = [];
  for (const p of sorted) {
    const last = merged[merged.length - 1];
    if (last && dayjs(p.startDate).diff(dayjs(last.expireDate), 'day') <= 1) {
      if (dayjs(p.expireDate).isAfter(dayjs(last.expireDate), 'day')) {
        last.expireDate = p.expireDate;
      }
    } else {
      merged.push({ ...p });
    }
  }
  return merged;
};

// 연체일수 계산 함수
const getOverdueDays = expireDate => {
  if (!expireDate) return 0;

  const expire = new Date(expireDate);
  const today = new Date();

  // 날짜만 비교 (시간 제외)
  expire.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const diffTime = today - expire;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  return diffDays > 0 ? diffDays : 0;
};

// Main Component
const LockerCard = ({ locker, index, onLockerClick, highlighted, elementId }) => {
  const status = getLockerStatus(locker);
  const config = STATUS_CONFIG[status];
  const overdueDays = status === 'expired' ? getOverdueDays(locker.expireDate) : 0;

  const handleClick = e => {
    e.preventDefault();
    onLockerClick(locker);
  };

  // 만료 상태일 때 날짜 영역 렌더링
  const renderDateSection = () => {
    if (status === 'expired' && locker.expireDate) {
      return (
        <>
          <OverdueDateText textColor={config.textColor}>{locker.expireDate}</OverdueDateText>
          <OverdueDaysText textColor={config.textColor}>( {overdueDays}일 연체 )</OverdueDaysText>
        </>
      );
    }

    // 이용권이 복수면 만료 기간은 빼고, 이용중이 있으면 이용중만 · 없으면 사용예정만 표시.
    // 이어지는 기간은 병합하고 실제 공백이 남을 때만 나열한다.
    if (locker.passPeriods?.length > 1) {
      const today = dayjs().startOf('day');
      const valid = locker.passPeriods.filter(p => !dayjs(p.expireDate).isBefore(today, 'day'));
      const current = valid.filter(p => !dayjs(p.startDate).isAfter(today, 'day'));
      const visible = current.length > 0 ? current : valid;
      if (visible.length > 0) {
        const merged = mergeAdjacentPeriods(visible);
        if (merged.length === 1) {
          return (
            <>
              <DateText textColor={config.textColor}>{merged[0].startDate}</DateText>
              <DateText textColor={config.textColor}>~ {merged[0].expireDate}</DateText>
            </>
          );
        }
        return merged.map((p, idx) => (
          <DateText key={idx} textColor={config.textColor}>
            {p.startDate?.slice(5)} ~ {p.expireDate?.slice(5)}
          </DateText>
        ));
      }
    }

    if (locker.issueDate) {
      return (
        <>
          <DateText textColor={config.textColor}>{locker.issueDate}</DateText>
          <DateText textColor={config.textColor}>~ {locker.expireDate}</DateText>
        </>
      );
    }

    return <EmptyDateText textColor={config.textColor}>-</EmptyDateText>;
  };

  return (
    <LockerCardContainer
      id={elementId}
      highlighted={highlighted}
      onClick={handleClick}
      cardBgColor={config.bgColor}
      cardBorder={config.border}>
      <LockerCardHeader>
        <LockerNumber textColor={config.textColor}>{locker.lockerNumber}</LockerNumber>
        <MemberName textColor={config.textColor}>
          {locker.memberName || locker.unregisteredMemberName || '-'}
        </MemberName>
      </LockerCardHeader>

      <LockerCardBody>{renderDateSection()}</LockerCardBody>

      <LockerCardFooter>
        <StatusBadge statusBorder={config.badgeBorder}>
          <StatusText statusColor={config.badgeColor}>{config.label}</StatusText>
        </StatusBadge>
      </LockerCardFooter>
    </LockerCardContainer>
  );
};

export default LockerCard;
